import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../../store/app/appStore';
import { getSectionName } from '../../../utils/getSectionName';

// Sections whose name does not naturally read back from the raw key on i18n
// miss — e.g. `TOP_WIN_GAMES` would otherwise fall back to "TOP WIN GAMES".
// Add an entry here when the snake-case key prettifies into the wrong words.
const FALLBACK_DISPLAY_NAMES: Record<string, string> = {
  TOP_WIN_GAMES: 'Top Wins',
};

const useGetTranslatedSectionName = (name: string) => {
  const { t } = useTranslation();
  const { restrictions } = useSettings();

  return useMemo(() => {
    // Defensive: BE has been observed to send sections with a missing `name`.
    // Nothing useful to translate / display in that case.
    if (!name) return '';

    const hasNoJackpotRestriction = restrictions.includes('noJackpot');
    const sectionName = getSectionName(name);
    const upperName = sectionName?.toUpperCase() ?? '';

    const noJackpotKey = `sections.no_jackpot_names.${upperName}`;
    const defaultKey = `sections.names.${upperName}`;
    const translationKey = hasNoJackpotRestriction && t(noJackpotKey, { defaultValue: '' }) ? noJackpotKey : defaultKey;
    const translated = t(translationKey);

    if (translationKey !== translated) {
      // Remove <pnl/> and <nl/> tags from translations
      return translated
        .replace(/<(pnl|nl)\s*\/>/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    return FALLBACK_DISPLAY_NAMES[upperName] ?? sectionName?.replace(/_/g, ' ') ?? '';
  }, [name, t, restrictions]);
};

export default useGetTranslatedSectionName;
